import { useCallback, useMemo } from "react";

import type { ForArrayType } from "@/components/features/_model/types/auto-generator-for-view-model";

import type { FormOptionType } from "@/components/ui/form/_types/type";

import type { CheckedState } from "@radix-ui/react-checkbox";

type Props<KEY_OF_VALUE_TYPE extends string> = {
	availableItemIDs: ForArrayType<string[]>;
	keyOfValue: KEY_OF_VALUE_TYPE;
	onSetViewModel: (
		values: {
			key: KEY_OF_VALUE_TYPE;
			value: ForArrayType<string[]>;
		}[],
	) => void;

	nameAndIDOptions: FormOptionType[];
};

export const useConnection = <KEY_OF_VALUE_TYPE extends string>({
	availableItemIDs,
	keyOfValue,
	onSetViewModel,

	nameAndIDOptions: _nameAndIDOptions,
}: Props<KEY_OF_VALUE_TYPE>) => {
	const rawAvailableItemIDs = useMemo(
		() => availableItemIDs.value.map((itemID) => itemID.value) ?? [],
		[availableItemIDs],
	);

	const nameAndIDOptions = useMemo(() => _nameAndIDOptions, [_nameAndIDOptions]);

	const onSetAvailableItemIDs = useCallback(
		(_input: CheckedState, item: FormOptionType) => {
			const availableItemID = item.value;

			const newRawAvailableItemIDs = rawAvailableItemIDs?.includes(availableItemID)
				? rawAvailableItemIDs.filter((item) => item !== availableItemID)
				: rawAvailableItemIDs?.length
					? [...rawAvailableItemIDs, availableItemID]
					: [availableItemID];

			const newAvailableItemIDs = {
				isEdited: true,
				hasError: false,
				value: newRawAvailableItemIDs?.map((itemID) => {
					return {
						isEdited: true,
						value: itemID,
					};
				}),
			};

			onSetViewModel([
				{
					key: keyOfValue,
					value: newAvailableItemIDs,
				},
			]);

			return newAvailableItemIDs;
		},
		[keyOfValue, onSetViewModel, rawAvailableItemIDs],
	);

	return {
		availableItemIDs,
		rawAvailableItemIDs,
		nameAndIDOptions,
		onSetAvailableItemIDs,
	};
};
